/**
 * TanStack Query key factories. Keep keys here so caches invalidate
 * consistently across hooks. Convention:
 *
 *   ["workspace"]                          → workspace info
 *   ["entity-types"]                       → entity type list
 *   ["entities", slug]                     → all entities of a type
 *   ["entity", slug, id]                   → one entity
 *   ["tool", name, args?]                  → raw tool call cache
 */

export const queryKeys = {
  workspace: () => ["workspace"] as const,
  entityTypes: () => ["entity-types"] as const,
  entityTypeBySlug: (slug: string) => ["entity-types", slug] as const,
  entities: (slug: string) => ["entities", slug] as const,
  entity: (slug: string, id: string) => ["entity", slug, id] as const,
  /** Sprint 12 — full row-detail payload (get_row_detail) for one record. */
  rowDetail: (slug: string, id: string) => ["row-detail", slug, id] as const,
  libraryNode: (id: string) => ["library-node", id] as const,
  libraryChildren: (parentId: string | null) => ["library-children", parentId ?? "root"] as const,
  libraryTree: () => ["library-tree"] as const,
  listChildren: (parentId: string) => ["list-children", parentId] as const,
  /** Sprint 5n — direct child nodes of one type (e.g. memories under an agent). */
  childNodes: (parentId: string, type: string) => ["child-nodes", parentId, type] as const,
  /** Sprint 5o — all pages in the workspace (subtree-wide), for the workspace cockpit. */
  workspacePages: () => ["workspace-pages"] as const,
  /** Sprint 11 — prompts visible from a scope (null → workspace-global root scope). */
  promptsInScope: (scopeNodeId: string | null) => ["prompts-in-scope", scopeNodeId ?? "root"] as const,
  /** Sprint 11 — every prompt in the workspace, for the /prompts dashboard. */
  allPrompts: () => ["all-prompts"] as const,
  /** Sprint 5n — files attached to a node (entity_ref). */
  files: (entityRef: string) => ["files", entityRef] as const,
  /** Sprint 5n — agent tasks, optionally subtree-wide, filtered by status. */
  agentTasks: (nodeId: string, status?: string, subtree?: boolean) =>
    ["agent-tasks", nodeId, status ?? "all", subtree ? "subtree" : "node"] as const,
  /** Sprint 7 — tasks assigned to the authenticated user, workspace-wide. */
  myTasks: (status?: string, includeCompleted?: boolean) =>
    ["my-tasks", status ?? "default", includeCompleted ? "with-completed" : "active"] as const,
  /** Sprint 8 — an agent's ranked priorities, by visibility scope. */
  agentPriorities: (nodeId: string, includeCompleted?: boolean, includePaused?: boolean) =>
    ["agent-priorities", nodeId, includeCompleted ? "with-completed" : "no-completed", includePaused ? "with-paused" : "no-paused"] as const,
  /** Sprint 14 — an agent's declared routines. */
  agentRoutines: (nodeId: string, enabled?: boolean) =>
    ["agent-routines", nodeId, enabled === undefined ? "all" : enabled ? "enabled" : "disabled"] as const,
  /** Sprint 14 — one routine. */
  routine: (id: string) => ["routine", id] as const,
  /** Sprint 14 — activities regarding a target object. */
  activitiesFor: (kind: string, id: string) => ["activities", "for", kind, id] as const,
  /** Sprint 14 — activities where an actor is the originator (cockpit panel). */
  activitiesByActor: (actorKind: string, actorId: string) =>
    ["activities", "actor", actorKind, actorId] as const,
  /** Sprint 7 — workspace members (mirrors useWorkspaceMembers' key). */
  members: () => ["workspace-members"] as const,
  views: (entityType?: string, viewType?: string) =>
    viewType
      ? (["views", entityType, viewType] as const)
      : entityType
        ? (["views", entityType] as const)
        : (["views"] as const),
  view: (id: string) => ["view", id] as const,
  /** Sprint 22 — chat sessions for the cockpit chat's history sidebar, keyed by scope. */
  chatSessions: (scopeKey: string) => ["chat-sessions", scopeKey] as const,
  /** Sprint 22 — one chat session's full payload (messages). */
  chatSession: (id: string) => ["chat-session", id] as const,
  /** Sprint 24.13 — owner-only prompt-cache health, by look-back window. */
  cacheHealth: (days: number) => ["cache-health", days] as const,
} as const;

/**
 * Tool-name helpers. The server synthesises tools named create_<slug>,
 * get_<slug>, etc. The console needs to derive these names from the entity
 * type definitions returned by list_entity_types.
 */

import type { EntityTypeDefinition } from "./types";

function snake(slug: string): string {
  return slug.replace(/-/g, "_");
}

/** "opportunity" → "opportunities"; "contact" → "contacts" */
export function pluralize(slug: string, override?: string): string {
  if (override) return override;
  if (/[^aeiou]y$/.test(slug)) return slug.slice(0, -1) + "ies";
  if (/(s|x|z|ch|sh)$/.test(slug)) return slug + "es";
  return slug + "s";
}

export function toolNames(type: EntityTypeDefinition) {
  const singular = snake(type.slug);
  const plural = snake(pluralize(type.slug, type.plural));
  return {
    create: `create_${singular}`,
    list: `list_${plural}`,
    get: `get_${singular}`,
    update: `update_${singular}`,
    archive: `archive_${singular}`,
  };
}

/** Field name of the user-visible "display field" or 'name' or 'id'. */
export function displayFieldFor(type: EntityTypeDefinition): string {
  const f = type.fields.find((x) => x.display_field);
  if (f) return f.name;
  if (type.fields.find((x) => x.name === "name")) return "name";
  return "id";
}
